import { Component, inject, Input, input, OnDestroy, OnInit } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../../forms/ziro-form-abstract.directive';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../../forms/ziro-forms-models';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../../forms/fields/select/ziro-select.component';
import { CustomCucmReport, Server } from '../../../../../shared/models/generated/ziroModels';
import { EMPTY, Observable, Subscriber, Subscription, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { CucmQueriesResource } from '../../../resources/cucm-queries.resource';
import { ToastService } from '../../../../../shared/services/toast.service';
import { ToastTypes } from '../../../../../shared/services/abstract/toast.service.abstract';
import { ZiroIcons } from '../../../../../shared/models/ziro-icons.enum';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../../../shared/bottom-nav/bottom-nav.service';
import { ButtonSizes, ButtonStyles } from '../../../../../button/button.component';
import { isEqual } from 'lodash';
import { HtmlInputType, ZiroTextConfig } from '../../../../../forms/fields/text/ziro-text.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ZiroModalService } from '../../../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ZiroCodeAreaConfig, ZiroCodeAreaMode } from '../../../../../forms/fields/code-area/ziro-code-area.component';
import { ZiroFormStateService } from '../../../../../forms/ziro-form-state.service';
import { HttpErrorResponse } from '@angular/common/http';
import { DatatableColumn, DatatableRow } from '../../../../../shared/tables/datatable/datatable.models';

interface CucmCustomReportDatatableRow extends DatatableRow {
  [key: string]: unknown;
}

export interface CucmReportForm {
  cucmServer: ZiroSelectOption;
  sqlQuery: string;
  reportName: string;
}

@Component({
  selector: 'ziro-cucm-report-builder-form',
  templateUrl: './cucm-report-builder-form.component.html',
  styleUrls: ['../../../../reporting.scss'],
  standalone: false,
})
export class CucmReportBuilderFormComponent
  extends ZiroFormAbstractDirective<CustomCucmReport, CucmReportForm>
  implements OnInit, OnDestroy
{
  private _cucmQueriesResource = inject(CucmQueriesResource);
  private _toastService = inject(ToastService);
  private _bottomNavService = inject(BottomNavService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  protected _ziroFormStateService: ZiroFormStateService;

  readonly servers = input<Server[]>([]);
  readonly isNewReport = input<boolean>();
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() report: CustomCucmReport;
  readonly allReports = input<CustomCucmReport[]>();
  buttonStyles = ButtonStyles;
  buttonSizes = ButtonSizes;
  ziroIcons = ZiroIcons;
  tableDataAutomation = 'cucm-report-builder-datatable';
  isTableLoading = true;
  hasQueryRun = false;
  formConfig: ZiroFormConfig;
  allReportNames: string[];
  serverIdLabelMap: { [id: number]: string } = {};
  queryResults: any[] = [];
  isTestQueryClicked = false;
  isSaveReportClicked = false;
  columns: DatatableColumn<CucmCustomReportDatatableRow>[];
  tableData: CucmCustomReportDatatableRow[];

  private _isInvalidQuery = false;
  private _invalidQueryMessage: string;
  private _subs = new Subscription();

  ngOnInit(): void {
    this._initFormConfig();
    this._initBottomNav();
    this.allReportNames = this.allReports().map((r) => r.reportName.trim().toLowerCase());
    this.servers().forEach((server) => {
      this.serverIdLabelMap[server.id] = server.description;
    });

    if (!this.isNewReport()) {
      this.entitySource.next(this.report);
    }

    this.ziroFormsUpdate$.subscribe((data: ZiroFormsUpdate<CustomCucmReport>) => {
      if (data.valid === ZiroFormsValidationState.VALID && this.isFormSubmitted) {
        this._bottomNavService.dispatch(
          new BottomNavUpdateState({
            hasValidationError: !this.isFormValid(),
          }),
        );
      }
      if (data?.old?.sqlQuery !== data?.new?.sqlQuery) {
        this.hasQueryRun = false;
      }
      if (data.old && !isEqual(data.old, data.new)) {
        this._isInvalidQuery = false;
        this.fieldChannels['sqlQuery'].validateSource.next();
      }
    });
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        reportName: {
          label: 'tkey;reports.custom_reports.custom_report_list.report_name',
          dataAutomation: 'cucm-report-builder-name-input',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: true,
          validation: [
            {
              validator: (val: string) => {
                if (!this.report) {
                  return this.allReportNames.includes(val.trim().toLowerCase())
                    ? ZiroFormsValidationState.INVALID
                    : ZiroFormsValidationState.VALID;
                } else {
                  return this.report.reportName === val || !this.allReportNames.includes(val.trim().toLowerCase())
                    ? ZiroFormsValidationState.VALID
                    : ZiroFormsValidationState.INVALID;
                }
              },
              message: 'tkey;reports.custom_reports.edit_report.duplicate_name_error',
            },
          ],
        },
        cucmServer: {
          label: 'tkey;reports.custom_reports.custom_report_list.cucm_server',
          dataAutomation: 'cucm-report-builder-server-input',
          componentConfig: new ZiroSelectConfig({
            options: this.servers()
              .filter((server: Server) => !server.disabled)
              .map((s: Server) => {
                return {
                  label: s.description,
                  value: s.id,
                };
              }),
          }),
          required: true,
        },
        sqlQuery: {
          label: 'tkey;reportmanagement.edit.query.label',
          dataAutomation: 'cucm-report-builder-query-input',
          componentConfig: new ZiroCodeAreaConfig({ mode: ZiroCodeAreaMode.SQL }),
          required: true,
          validation: [
            {
              validator: () =>
                !this._isInvalidQuery ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
              message: () => this._invalidQueryMessage,
            },
          ],
        },
      },
    } as ZiroFormConfig;
  }

  private _initBottomNav: () => void = () => {
    const buttons = [
      {
        id: 'cucmReportBuilderTestQueryButton',
        dataAutomation: 'cucm-report-builder-test-query-button',
        label: 'tkey;menu.report.cucm_report_builder.button.test',
        icon: ZiroIcons.RUN,
        buttonClass: ButtonStyles.INFO,
        cb: () => {
          this.testQueryClicked();
        },
      },
      {
        id: 'cucmReportBuilderExportButton',
        dataAutomation: 'cucm-report-builder-export-button',
        label: 'tkey;reports.xlsx_export.button',
        icon: ZiroIcons.EXPORT,
        buttonClass: ButtonStyles.INFO,
        cb: () => {
          this._onExportClicked();
        },
      },
      {
        id: 'cucmReportBuilderCancelButton',
        dataAutomation: 'cucm-report-builder-cancel-button',
        label: 'tkey;reportmanagement.edit.query.cancel.button',
        buttonClass: ButtonStyles.DEFAULT,
        cb: () => {
          this._returnToListView();
        },
      },
      {
        id: 'cucmReportBuilderSaveReportButton',
        dataAutomation: 'cucm-report-builder-save-report-button',
        label: 'tkey;global.button.save.text',
        icon: ZiroIcons.SAVE_SUCCESS,
        buttonClass: ButtonStyles.PRIMARY,
        cb: () => {
          this.isSaveReportClicked = true;
          this._bottomNavService.dispatch(
            new BottomNavUpdateState({
              hasValidationError: !this.isFormValid(),
            }),
          );
          this._validateAndSubmitSource.next(true);
        },
      },
    ];

    if (!this.isNewReport()) {
      buttons.splice(3, 0, {
        id: 'bottom-nav-delete-button',
        dataAutomation: 'bottom-nav-delete-button',
        label: 'tkey;global.button.delete.text',
        icon: ZiroIcons.DELETE_SMALL,
        buttonClass: ButtonStyles.DANGER,
        cb: () => {
          this._onDeleteClicked();
        },
      });
    }

    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(buttons));
  };

  testQueryClicked(): void {
    if (this.isFormValid()) {
      this.isTestQueryClicked = true;
    }
    this._validateAndSubmitSource.next(true);
  }

  private _hasQueryChanged(): boolean {
    return (
      this.report && (this.report.sqlQuery !== this.entity.sqlQuery || this.report.serverId !== this.entity.serverId)
    );
  }

  private _testQuery(isModalCall = false): Observable<any> {
    if (!isModalCall) {
      this._setPendingButton('cucmReportBuilderTestQueryButton', true);
      this._setDisabledButtons(
        ['cucmReportBuilderCancelButton', 'cucmReportBuilderSaveReportButton', 'cucmReportBuilderExportButton'],
        true,
      );
    }
    this.queryResults = [];
    this.isTableLoading = true;
    this.hasQueryRun = false;
    const serverID = this.servers().find((s) => s.id === this.formData.cucmServer?.value).id;
    return new Observable((subscriber: Subscriber<any>) => {
      this._cucmQueriesResource.postLiveQuery({ query: this.entity.sqlQuery, cucmId: serverID }).subscribe({
        next: (response) => {
          this.queryResults = response.results;
          this.hasQueryRun = true;
          this._initTable();
          this._toastService.push(
            ToastTypes.SUCCESS,
            ZiroIcons.TABLE,
            'tkey;menu.report.cucm_report_builder.modal.success.title',
            'tkey;menu.report.cucm_report_builder.modal.success.message',
          );

          subscriber.next(response);
          subscriber.complete();
        },
        error: (response) => {
          this.isTableLoading = false;
          subscriber.error(response);
          subscriber.complete();
        },
      });
    });
  }

  private _saveReport(isModalCall = false): Observable<void> {
    if (!isModalCall) {
      this._setPendingButton('cucmReportBuilderSaveReportButton', true);
      this._setDisabledButtons(
        ['cucmReportBuilderCancelButton', 'cucmReportBuilderTestQueryButton', 'cucmReportBuilderExportButton'],
        true,
      );
    }

    const request = this.isNewReport()
      ? this._cucmQueriesResource.postNewReport(this.entity).pipe(
          map((newId) => {
            this.entity.id = Number(newId);
          }),
        )
      : this._cucmQueriesResource.updateReport(this.entity.id, this.entity).pipe(
          map(() => {
            this.report = this.entity;
          }),
        );

    return request.pipe(
      tap(() => {
        this._toastService.pushSaveToast(
          'tkey;reports.custom_reports.custom_report_list.toast.title',
          this.entity.reportName,
          ZiroIcons.TABLE,
        );
      }),
    );
  }

  private _initTable(): void {
    if (this.queryResults.length > 0) {
      this.columns = Object.keys(this.queryResults[0]).map(
        (queryKey) =>
          ({
            name: queryKey,
            label: queryKey,
          }) as DatatableColumn<CucmCustomReportDatatableRow>,
      );
      this.tableData = this.queryResults;
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
      }),
    );
    this._subs.unsubscribe();
  }

  toFormData = (entity: CustomCucmReport): CucmReportForm => {
    const server = this.servers().find((server) => server.id === entity.serverId);
    return {
      cucmServer: {
        label: server.description,
        value: server.id,
        disabled: server.disabled,
      },
      sqlQuery: entity.sqlQuery,
      reportName: entity.reportName,
    };
  };

  toEntity = (formData: CucmReportForm): CustomCucmReport => {
    const server = this.servers().find((s) => s.id === formData.cucmServer?.value);
    return {
      id: this.report ? this.report.id : null,
      lastUpdated: this.report ? this.report.lastUpdated : null,
      reportName: formData.reportName,
      serverId: server ? server.id : null,
      sqlQuery: formData.sqlQuery,
    };
  };

  private _onExportClicked(): void {
    Object.keys(this.fieldChannels).forEach((key) => {
      this.fieldChannels[key].validateSource.next();
    });
    if (!this.isFormValid()) {
      return;
    }
    if (this.isNewReport() || this._hasQueryChanged()) {
      const options = {
        windowClass: 'custom-report-run-and-save-report-modal',
        modalViewProperties: {
          title: this._translateService.instant('tkey;reports.custom_reports.modal.title'),
          promptBody: this._translateService.instant('tkey;reports.custom_reports.modal_2.text'),
          icon: ZiroIcons.DOWNLOAD,
          displayCloseButton: true,
          buttons: [
            {
              label: 'tkey;dialogs.button.cancel',
              buttonClass: ButtonStyles.OUTLINE_DEFAULT,
              dataAutomation: 'confirmation-modal-cancel-button',
            },
            {
              label: 'tkey;reports.custom_reports.modal_2.button',
              buttonClass: ButtonStyles.OUTLINE_PRIMARY,
              dataAutomation: 'confirmation-modal-confirm-button',
              cb: () => {
                return this._testQuerySaveAndExport().pipe(
                  tap(() => {
                    this._ziroFormStateService.setIsFormDirty(false);
                    this._returnToListView();
                  }),
                  catchError((response) => {
                    this._handleErrorResponse(response);
                    return throwError(() => response);
                  }),
                );
              },
            },
          ],
        },
      };
      this._ziroModalService.openPromptModal(() => options.modalViewProperties, options).subscribe();
    } else {
      this._exportFile().subscribe(() => this._resetButtonStates());
    }
  }

  private _testQuerySaveAndExport(): Observable<void> {
    return new Observable<void>((subscriber: Subscriber<HttpErrorResponse | void>) => {
      this._testQuery(true)
        .pipe(mergeMap(() => this._saveReport(true)))
        .pipe(mergeMap(() => this._exportFile(true)))
        .subscribe({
          next: () => {
            subscriber.next();
            subscriber.complete();
          },
          error: (response) => {
            subscriber.error(response);
            subscriber.complete();
          },
        });
    });
  }

  private _handleErrorResponse(error: any): void {
    this._isInvalidQuery = true;
    this._invalidQueryMessage = error.error.description;
    this.fieldChannels['sqlQuery'].validateSource.next();
    this._toastService.push(
      ToastTypes.ERROR,
      ZiroIcons.TABLE,
      'tkey;menu.report.cucm_report_builder.modal.error.title',
      'tkey;menu.report.cucm_report_builder.modal.error.message',
    );
    this._resetButtonStates();
  }

  private _resetButtonStates(): void {
    this._setDisabledButtons(
      [
        'cucmReportBuilderCancelButton',
        'cucmReportBuilderTestQueryButton',
        'cucmReportBuilderSaveReportButton',
        'cucmReportBuilderExportButton',
      ],
      false,
    );
    this.isTableLoading = false;
    this._validateAndSubmitSource.next(false);
  }

  private _exportFile(isModalCall = false): Observable<void> {
    if (!isModalCall) {
      this._setPendingButton('cucmReportBuilderExportButton', true);
      this._setDisabledButtons(
        ['cucmReportBuilderCancelButton', 'cucmReportBuilderTestQueryButton', 'cucmReportBuilderSaveReportButton'],
        true,
      );
    }
    return this._cucmQueriesResource.getExportToExcel(this.entity.id).pipe(
      tap(() => {
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.EXPORT,
          'tkey;reports.xlsx_export.downloaded_toast.title',
          'tkey;reports.xlsx_export.downloaded_toast.message',
        );
      }),
    );
  }

  private _setPendingButton(buttonId: string, setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: buttonId,
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }

  private _returnToListView(): void {
    this._router.navigate(['../'], { relativeTo: this._route });
  }

  private _setDisabledButtons(buttonIds: string[], setting: boolean): void {
    buttonIds.forEach((id) => this._setDisabledButton(id, setting));
  }

  private _setDisabledButton(buttonId: string, setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: buttonId,
        state: {
          pending: false,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }

  private _showConfirmationModal(): Observable<unknown> {
    return new Observable((subscriber) => {
      const options = {
        windowClass: 'custom-report-run-and-save-report-modal',
        modalViewProperties: {
          title: this._translateService.instant('tkey;reports.custom_reports.modal.title'),
          promptBody: this._translateService.instant('tkey;reports.custom_reports.modal_1.text'),
          icon: ZiroIcons.TABLE,
          displayCloseButton: true,
          buttons: [
            {
              label: 'tkey;dialogs.button.cancel',
              buttonClass: ButtonStyles.DEFAULT,
              dataAutomation: 'confirmation-modal-cancel-button',
            },
            {
              label: 'tkey;reports.custom_reports.modal_1.button',
              buttonClass: ButtonStyles.PRIMARY,
              dataAutomation: 'confirmation-modal-confirm-button',
              cb: () => {
                return this._testQueryAndSave().pipe(
                  tap(() => {
                    subscriber.next(true);
                    subscriber.complete();
                  }),
                  catchError((response) => {
                    this._handleErrorResponse(response);
                    subscriber.error(response);
                    subscriber.complete();
                    return throwError(() => response);
                  }),
                );
              },
            },
          ],
        },
      };
      this._ziroModalService
        .openPromptModal(() => options.modalViewProperties, options)
        .subscribe((response) => {
          if (response === false) {
            subscriber.next(false);
            subscriber.complete();
          }
        });
    });
  }

  private _testQueryAndSave(): Observable<void> {
    return new Observable<void>((subscriber: Subscriber<HttpErrorResponse | void>) => {
      this._testQuery(true)
        .pipe(mergeMap(() => this._saveReport(true)))
        .subscribe({
          next: () => {
            subscriber.next();
            subscriber.complete();
          },
          error: (response) => {
            subscriber.error(response);
            subscriber.complete();
          },
        });
    });
  }

  protected submit(): Observable<void> | Observable<unknown> {
    if (this.isTestQueryClicked) {
      this.isTestQueryClicked = false;
      return this._testQuery().pipe(
        tap(() => {
          setTimeout(() => {
            if (document.getElementById('query-results-header')) {
              document.getElementById('query-results-header').scrollIntoView({ behavior: 'smooth' });
            }
          });
          this._resetButtonStates();
        }),
        catchError((response) => {
          this._handleErrorResponse(response);
          return throwError(() => EMPTY);
        }),
      );
    }
    if (this.isSaveReportClicked) {
      this.isSaveReportClicked = false;
      if (!this.hasQueryRun && (this.isNewReport() || this._hasQueryChanged())) {
        return new Observable((subscriber) => {
          this._showConfirmationModal().subscribe({
            next: (isSuccess) => {
              if (isSuccess) {
                this._returnToListView();
                subscriber.next(true);
                subscriber.complete();
              } else {
                subscriber.error(EMPTY);
                subscriber.complete();
              }
            },
            error: (response) => {
              subscriber.error(response);
              subscriber.complete();
            },
          });
        });
      } else {
        return this._saveReport().pipe(
          tap(() => {
            this._returnToListView();
          }),
          catchError((response) => {
            this._handleErrorResponse(response);
            return throwError(() => EMPTY);
          }),
        );
      }
    }
  }

  private _onDeleteClicked(): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: 'text-danger',
        title: this._translateService.instant('tkey;reports.custom_reports.custom_report_list.delete.modal.title'),
        promptBody: this._translateService.instant(
          'tkey;reports.custom_reports.custom_report_list.delete.modal.message',
          {
            reportName: this.entity.reportName,
          },
        ),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              return this._deleteReport();
            },
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _deleteReport(): Observable<void> {
    return new Observable((subscriber: Subscriber<void>) => {
      this._cucmQueriesResource.deleteReport(this.entity.id).subscribe(() => {
        this._ziroFormStateService.setIsFormDirty(false);
        this._toastService.pushDeleteToast(
          'tkey;reports.custom_reports.custom_report_list.toast.title',
          this.entity.reportName,
        );
        this._returnToListView();
        subscriber.next(null);
        subscriber.complete();
      });
    });
  }
}
